import React from 'react';
import QRCode from 'react-qr-code';
import { Ban, CalendarDays, Clock, ExternalLink, IdCard, KeyRound, ShieldCheck, Users } from 'lucide-react';
import type { PaymentLinkPass } from '../../../api/paymentLinkApi';
import type { Lang } from '../../../i18n/publicEvents';
import type { PayLinkStringKey } from './payLinkStrings';
import { passViewFor } from './paymentLinkModel';

// SAM-633 — the entry pass on the paid screen of the public pay page. The QR is drawn here from
// `pass.qr_value` (the access id the gate resolves — booking_access.public_pass_view); nothing is
// fetched, stored or logged. `pass_url` is the tenant's public pass page, which outlives this link.

type T = (key: PayLinkStringKey, vars?: Record<string, string | number>) => string;

const linkButton =
  'btn primary mt-3 flex w-full items-center justify-center gap-2 !py-3 !text-[15px] no-underline';

export const PaymentLinkPassCard: React.FC<{ pass: PaymentLinkPass | null | undefined; lang: Lang; t: T }> = ({ pass, lang, t }) => {
  const view = passViewFor(pass, lang);
  if (view.kind === 'none') return null;

  if (view.kind === 'verify') {
    return (
      <section className="mb-3 rounded-[12px] border border-[var(--warn-line)] bg-[var(--warn-bg-2)] p-4" data-testid="paylink-pass" data-state="verification_required">
        <div className="mb-2 flex items-center gap-2 text-[var(--warn-fg)]">
          <IdCard size={20} className="flex-none" />
          <h2 className="m-0 text-[16px] font-semibold">{t('passVerifyTitle')}</h2>
        </div>
        <p className="m-0 text-[14px] leading-relaxed text-ink-700">{t('passVerifyBody')}</p>
        {view.window ? (
          <p className="m-0 mt-2 flex items-center gap-2 text-[13px] text-ink-500"><CalendarDays size={14} className="flex-none" />{t('passValid')}: <span className="first-letter:uppercase">{view.window}</span></p>
        ) : null}
        {view.passUrl ? (
          <a className={linkButton} href={view.passUrl} target="_blank" rel="noopener noreferrer" data-testid="paylink-pass-verify">
            {t('passVerifyButton')} <ExternalLink size={16} />
          </a>
        ) : null}
      </section>
    );
  }

  if (view.kind !== 'qr') {
    const copy = {
      used: ['passUsedTitle', 'passUsedBody'],
      cancelled: ['passCancelledTitle', 'passCancelledBody'],
      unavailable: ['passUnavailableTitle', 'passUnavailableBody'],
    }[view.kind] as [PayLinkStringKey, PayLinkStringKey];
    return (
      <section className="mb-3 rounded-[12px] border border-[var(--line)] bg-white p-4" data-testid="paylink-pass" data-state={view.kind}>
        <div className="mb-1 flex items-center gap-2">
          {view.kind === 'unavailable' ? <Clock size={18} className="flex-none text-ink-500" /> : <Ban size={18} className="flex-none text-ink-500" />}
          <h2 className="m-0 text-[15px] font-semibold">{t(copy[0])}</h2>
        </div>
        <p className="m-0 text-[14px] leading-relaxed text-ink-500">{t(copy[1])}</p>
      </section>
    );
  }

  return (
    <section
      className="mb-3 overflow-hidden rounded-[12px] bg-white"
      style={{ border: '1px solid var(--line)' }}
      data-testid="paylink-pass"
      data-state="ready"
    >
      {/* Explicit borders: this app runs without Tailwind preflight, so border-* utilities alone draw nothing. */}
      <div className="px-4 pb-3 pt-4 text-center" style={{ borderBottom: '1px dashed var(--line)' }}>
        <h2 className="m-0 text-[17px] font-semibold">{t('passTitle')}</h2>
        <p className="paylink-noprint m-0 mt-1 text-[13px] text-ink-500">{t('passShowQr')}</p>
      </div>
      <div className="paylink-noprint flex justify-center px-4 py-5">
        <div className="rounded-[12px] bg-white p-3" style={{ border: '1px solid var(--line)' }} data-testid="paylink-pass-qr">
          <QRCode value={view.qrValue} size={208} level="M" style={{ width: '208px', height: '208px', display: 'block' }} />
        </div>
      </div>
      <dl className="m-0 grid grid-cols-1 gap-2 px-4 pb-4 text-[14px]">
        {view.window ? (
          <PassRow icon={<CalendarDays size={16} />} label={t('passValid')} value={view.window} capitalize />
        ) : null}
        {view.guests ? <PassRow icon={<Users size={16} />} label={t('passGuests')} value={String(view.guests)} /> : null}
        {view.children ? <PassRow icon={<Users size={16} />} label={t('passChildren')} value={String(view.children)} /> : null}
        {view.doorPin ? (
          <PassRow
            icon={<KeyRound size={16} />}
            label={t('passDoorPin')}
            value={view.doorPin.pin || t('passDoorPinPending')}
            mono={!!view.doorPin.pin}
            noPrint
          />
        ) : null}
      </dl>
      <div className="bg-[var(--surface)] px-4 py-3" style={{ borderTop: '1px solid var(--line)' }}>
        <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 text-[13px] leading-relaxed text-ink-700">
          {!view.idVerified && (
            <li className="flex items-start gap-2"><ShieldCheck size={15} className="mt-[2px] flex-none text-ink-500" />{t('passBringId')}</li>
          )}
          <li className="paylink-noprint flex items-start gap-2"><Clock size={15} className="mt-[2px] flex-none text-ink-500" />{t('passSaveHint')}</li>
        </ul>
        {view.passUrl ? (
          <a className={`${linkButton} paylink-noprint`} href={view.passUrl} target="_blank" rel="noopener noreferrer" data-testid="paylink-pass-open">
            {t('passOpen')} <ExternalLink size={16} />
          </a>
        ) : null}
      </div>
    </section>
  );
};

const PassRow: React.FC<{ icon: React.ReactNode; label: string; value: string; mono?: boolean; capitalize?: boolean; noPrint?: boolean }> = ({ icon, label, value, mono, capitalize, noPrint }) => (
  <div className={`${noPrint ? 'paylink-noprint ' : ''}flex items-start justify-between gap-3 border-b border-[var(--line-subtle)] pb-2 last:border-b-0`}>
    <dt className="flex items-center gap-2 text-[13px] text-ink-500"><span className="flex-none">{icon}</span>{label}</dt>
    <dd className={`m-0 text-right font-medium ${mono ? 'font-mono text-[18px] tracking-widest' : ''} ${capitalize ? 'first-letter:uppercase' : ''}`}>{value}</dd>
  </div>
);

export default PaymentLinkPassCard;
